<template>
  <div class="usage">
    <div class="usage__head">
      <div>
        <h1 class="page-title">消耗明细</h1>
        <p class="page-desc">
          开放平台调用记录：积分变动、耗时、任务 ID、提示词与结果。
        </p>
      </div>
      <div class="usage__head-actions">
        <router-link class="btn btn-primary" to="/recharge">充值积分</router-link>
        <button type="button" class="btn btn-ghost" :disabled="loading" @click="reload">
          {{ loading ? '加载中…' : '刷新' }}
        </button>
      </div>
    </div>

    <div class="stats">
      <div class="stat panel">
        <div class="stat__label">当前可用积分</div>
        <div class="stat__value mono">{{ balanceText }}</div>
        <router-link class="stat__link" to="/recharge">去充值 →</router-link>
      </div>
      <div class="stat panel">
        <div class="stat__label">近 30 日消耗</div>
        <div class="stat__value mono err">-{{ summary.consume }}</div>
      </div>
      <div class="stat panel">
        <div class="stat__label">近 30 日返还</div>
        <div class="stat__value mono ok">+{{ summary.refund }}</div>
      </div>
      <div class="stat panel">
        <div class="stat__label">近 30 日净消耗</div>
        <div class="stat__value mono">{{ summary.net }}</div>
      </div>
    </div>

    <div class="filter-row">
      <button
        v-for="t in typeTabs"
        :key="t.value"
        type="button"
        class="chip"
        :class="{ 'is-active': typeFilter === t.value }"
        @click="setType(t.value)"
      >
        {{ t.label }}
      </button>
    </div>

    <p v-if="error" class="flash is-err">{{ error }}</p>

    <div v-if="loading && !list.length" class="panel empty">加载中…</div>
    <div v-else-if="!list.length" class="panel empty">暂无开放平台消耗记录</div>

    <div v-else class="panel table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th>时间</th>
            <th>类型</th>
            <th>状态</th>
            <th>积分</th>
            <th>耗时</th>
            <th>任务 ID</th>
            <th>提示词</th>
            <th>结果</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in list" :key="row.id || row.uuid">
            <td class="mono muted nowrap">{{ formatDate(row.created_at) }}</td>
            <td>
              <span class="tag" :class="row.type === 1 ? 'tag-in' : 'tag-out'">
                {{ row.type_label || (row.type === 1 ? '返还' : '消耗') }}
              </span>
            </td>
            <td>
              <span class="status" :class="'status--' + statusTone(row.status)">
                {{ statusLabel(row.status) }}
              </span>
            </td>
            <td class="mono" :class="row.type === 1 ? 'ok' : 'err'">
              {{ row.type === 1 ? '+' : '-' }}{{ Math.abs(row.value || 0) }}
            </td>
            <td class="mono muted nowrap">{{ formatLatency(row) }}</td>
            <td class="mono task-id" :title="row.task_id || ''">
              {{ row.task_id || '—' }}
            </td>
            <td class="prompt" :title="row.prompt || ''">
              {{ truncate(row.prompt, 80) }}
            </td>
            <td class="result">
              <button
                v-if="hasResult(row)"
                type="button"
                class="btn-result"
                @click="openDetail(row)"
              >
                查看结果
              </button>
              <span v-else class="muted">—</span>
            </td>
            <td class="desc">
              <div>{{ row.description || row.model_id || '—' }}</div>
              <div v-if="row.api_path" class="sub mono muted">{{ row.api_path }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="total > 0" class="pager">
      <button type="button" class="btn btn-ghost" :disabled="page <= 1 || loading" @click="goPage(page - 1)">
        上一页
      </button>
      <span class="mono muted">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
      <button
        type="button"
        class="btn btn-ghost"
        :disabled="page >= totalPages || loading"
        @click="goPage(page + 1)"
      >
        下一页
      </button>
    </div>

    <!-- 详情弹窗 -->
    <div v-if="detail" class="detail-mask" @click.self="closeDetail">
      <div class="detail" role="dialog" aria-modal="true" aria-label="详情">
        <div class="detail__head">
          <h2 class="detail__title">详情</h2>
          <button type="button" class="detail__close" aria-label="关闭" @click="closeDetail">×</button>
        </div>

        <div class="detail__body">
          <section class="detail__col detail__col--in">
            <div class="detail__col-head">
              <span class="detail__col-title">输入</span>
              <button type="button" class="detail__ghost-btn" @click="copyJson">复制 JSON</button>
            </div>

            <div class="detail__block">
              <div class="detail__label">提示词</div>
              <div class="detail__prompt">{{ detail.prompt || '—' }}</div>
            </div>

            <div class="detail__block">
              <div class="detail__label">参数</div>
              <div class="detail__params">
                <div v-for="item in detailParams" :key="item.key" class="detail__param">
                  <span class="detail__param-k">{{ item.key }}</span>
                  <span class="detail__param-v mono">{{ item.value }}</span>
                </div>
                <div v-if="!detailParams.length" class="muted">—</div>
              </div>
            </div>

            <div class="detail__meta">
              <div class="detail__meta-row">
                <span>任务ID</span>
                <span class="mono">{{ detail.task_id || '—' }}</span>
              </div>
              <div class="detail__meta-row">
                <span>平台</span>
                <span>{{ detailPlatform }}</span>
              </div>
              <div class="detail__meta-row">
                <span>创建时间</span>
                <span>{{ formatDateSlash(detail.created_at) }}</span>
              </div>
              <div class="detail__meta-row">
                <span>完成时间</span>
                <span>{{ formatDateSlash(detail.completed_at || detail.updated_at) }}</span>
              </div>
            </div>
          </section>

          <section class="detail__col detail__col--out">
            <div class="detail__col-head">
              <span class="detail__col-title">输出</span>
            </div>

            <div class="detail__preview">
              <video
                v-if="isVideoResult"
                :src="detailResultUrl"
                controls
                class="detail__media"
              />
              <img
                v-else-if="detailResultUrl"
                :src="detailResultUrl"
                alt="result"
                class="detail__media"
              />
              <div v-else class="detail__preview-empty muted">暂无预览</div>
            </div>

            <div class="detail__block">
              <div class="detail__label">结果地址</div>
              <div class="detail__url-row">
                <input class="detail__url mono" type="text" readonly :value="detailResultUrl || ''" />
                <button
                  type="button"
                  class="detail__icon-btn"
                  title="复制"
                  :disabled="!detailResultUrl"
                  @click="copyText(detailResultUrl)"
                >
                  复制
                </button>
              </div>
            </div>

            <a
              v-if="detailResultUrl"
              class="detail__source-btn"
              :href="detailResultUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              查看源文件
            </a>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchOpenGateUsage, pickUsagePayload } from '../utils/usageApi'
import { showSuccessToast, showErrorToast } from '../utils/toast'

export default {
  name: 'Usage',
  data() {
    return {
      loading: false,
      error: '',
      list: [],
      page: 1,
      perPage: 20,
      total: 0,
      scoreBalance: null,
      summary: { consume: 0, refund: 0, net: 0 },
      typeFilter: 'all',
      typeTabs: [
        { value: 'all', label: '全部' },
        { value: 'out', label: '消耗' },
        { value: 'in', label: '返还' }
      ],
      pollTimer: null,
      detail: null
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage) || 1)
    },
    balanceText() {
      return this.scoreBalance != null ? Number(this.scoreBalance).toLocaleString() : '—'
    },
    hasPendingTasks() {
      return this.list.some(
        row => row.task_id && this.isPendingStatus(row.status) && !row.result_url
      )
    },
    detailResultUrl() {
      if (!this.detail) return ''
      return this.resolveResultUrl(this.detail)
    },
    isVideoResult() {
      const u = this.detailResultUrl
      if (!u) return false
      if (/\.(mp4|webm|mov)(\?|$)/i.test(u)) return true
      const path = String((this.detail && this.detail.api_path) || '')
      const model = String((this.detail && this.detail.model_id) || '')
      return /seedance|video|kling_omni|kling_motion|sora|veo|grok/i.test(path + ' ' + model)
    },
    detailPlatform() {
      if (!this.detail) return '—'
      const req = this.detail.request || {}
      if (req.platform) return String(req.platform)
      const m = String(this.detail.model_id || '').toLowerCase()
      if (m.includes('gpt-image') || m.includes('openai')) return 'openai'
      if (m.includes('seedance') || m.includes('doubao')) return 'doubao'
      if (m.includes('kling')) return 'kling'
      if (m.includes('midjourney') || m.includes('mj')) return 'midjourney'
      return 'idoing'
    },
    detailParams() {
      if (!this.detail) return []
      const req = Object.assign({}, this.detail.request || {})
      delete req.prompt
      delete req.platform
      if (!req.model && this.detail.model_id) req.model = this.detail.model_id
      if (req.duration == null && this.detail.duration_sec != null) {
        req.duration = this.detail.duration_sec
      }
      const order = ['model', 'size', 'quality', 'duration', 'ratio', 'aspect_ratio', 'resolution']
      const keys = Object.keys(req).filter(k => req[k] != null && req[k] !== '')
      keys.sort((a, b) => {
        const ia = order.indexOf(a)
        const ib = order.indexOf(b)
        if (ia === -1 && ib === -1) return a.localeCompare(b)
        if (ia === -1) return 1
        if (ib === -1) return -1
        return ia - ib
      })
      return keys.map(k => ({
        key: k,
        value: typeof req[k] === 'object' ? JSON.stringify(req[k]) : String(req[k])
      }))
    }
  },
  created() {
    this.load()
    this._onKey = e => {
      if (e.key === 'Escape') this.closeDetail()
    }
    window.addEventListener('keydown', this._onKey)
  },
  beforeDestroy() {
    this.clearPoll()
    window.removeEventListener('keydown', this._onKey)
    document.body.style.overflow = ''
  },
  methods: {
    clearPoll() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    schedulePoll() {
      this.clearPoll()
      if (!this.hasPendingTasks) return
      this.pollTimer = setTimeout(() => {
        this.load({ silent: true })
      }, 5000)
    },
    setType(v) {
      if (this.typeFilter === v) return
      this.typeFilter = v
      this.page = 1
      this.load()
    },
    reload() {
      this.load()
    },
    goPage(p) {
      this.page = p
      this.load()
    },
    hasResult(row) {
      return !!this.resolveResultUrl(row)
    },
    resolveResultUrl(row) {
      if (!row) return ''
      if (row.result_url) return String(row.result_url)
      const r = row.result
      if (!r || typeof r !== 'object') return ''
      if (r.video_url) return String(r.video_url)
      if (r.url) return String(r.url)
      if (Array.isArray(r.urls) && r.urls.length) {
        const first = r.urls[0]
        return typeof first === 'string' ? first : String((first && first.url) || '')
      }
      return ''
    },
    openDetail(row) {
      this.detail = row
      document.body.style.overflow = 'hidden'
    },
    closeDetail() {
      this.detail = null
      document.body.style.overflow = ''
    },
    async copyText(text) {
      const t = String(text || '')
      if (!t) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(t)
        } else {
          const ta = document.createElement('textarea')
          ta.value = t
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        showSuccessToast('已复制')
      } catch (e) {
        showErrorToast('复制失败')
      }
    },
    copyJson() {
      if (!this.detail) return
      const payload = {
        task_id: this.detail.task_id || '',
        prompt: this.detail.prompt || '',
        status: this.detail.status || '',
        model_id: this.detail.model_id || '',
        api_path: this.detail.api_path || '',
        request: this.detail.request || {},
        result_url: this.detailResultUrl,
        created_at: this.detail.created_at,
        completed_at: this.detail.completed_at || this.detail.updated_at
      }
      this.copyText(JSON.stringify(payload, null, 2))
    },
    truncate(v, n) {
      const s = String(v || '').trim()
      if (!s) return '—'
      return s.length > n ? s.slice(0, n) + '…' : s
    },
    statusLabel(raw) {
      const s = String(raw || '').trim().toLowerCase()
      if (!s) return '—'
      const map = {
        submitted: '已提交',
        pending: '排队中',
        queued: '排队中',
        processing: '处理中',
        running: '处理中',
        success: '成功',
        succeeded: '成功',
        completed: '成功',
        done: '成功',
        failed: '失败',
        error: '失败',
        cancelled: '已取消',
        canceled: '已取消',
        refunded: '已返还',
        ok: '完成'
      }
      return map[s] || String(raw)
    },
    statusTone(raw) {
      const s = String(raw || '').trim().toLowerCase()
      if (['success', 'succeeded', 'completed', 'done', 'ok'].includes(s)) return 'ok'
      if (['failed', 'error', 'cancelled', 'canceled', 'refunded'].includes(s)) return 'err'
      if (['submitted', 'pending', 'queued', 'processing', 'running'].includes(s)) return 'wait'
      return 'muted'
    },
    isPendingStatus(raw) {
      const s = String(raw || '').trim().toLowerCase()
      return ['submitted', 'pending', 'queued', 'processing', 'running'].includes(s)
    },
    formatLatency(row) {
      const ms = row.cost_ms != null ? Number(row.cost_ms) : row.latency_ms != null ? Number(row.latency_ms) : null
      if (ms == null || Number.isNaN(ms) || ms < 0) return '—'
      if (ms < 1000) return ms + 'ms'
      return (ms / 1000).toFixed(ms >= 10000 ? 1 : 2) + 's'
    },
    formatDate(v) {
      if (!v) return '—'
      const d = new Date(String(v).replace(/-/g, '/'))
      if (Number.isNaN(d.getTime())) return String(v)
      const pad = n => String(n).padStart(2, '0')
      return (
        d.getFullYear() +
        '-' +
        pad(d.getMonth() + 1) +
        '-' +
        pad(d.getDate()) +
        ' ' +
        pad(d.getHours()) +
        ':' +
        pad(d.getMinutes()) +
        ':' +
        pad(d.getSeconds())
      )
    },
    formatDateSlash(v) {
      if (!v) return '—'
      const d = new Date(String(v).replace(/-/g, '/'))
      if (Number.isNaN(d.getTime())) return String(v)
      return (
        d.getFullYear() +
        '/' +
        (d.getMonth() + 1) +
        '/' +
        d.getDate() +
        ' ' +
        String(d.getHours()).padStart(2, '0') +
        ':' +
        String(d.getMinutes()).padStart(2, '0') +
        ':' +
        String(d.getSeconds()).padStart(2, '0')
      )
    },
    async load(opts = {}) {
      const silent = !!opts.silent
      if (!silent) {
        this.loading = true
        this.error = ''
      }
      try {
        const res = await fetchOpenGateUsage({
          page: this.page,
          per_page: this.perPage,
          type: this.typeFilter
        })
        const data = pickUsagePayload(res) || {}
        this.list = Array.isArray(data.list) ? data.list : []
        this.total = Number(data.item_total) || 0
        this.page = Number(data.page) || this.page
        if (data.score_balance != null) this.scoreBalance = Number(data.score_balance)
        const s = data.summary_30d || {}
        this.summary = {
          consume: Number(s.consume) || 0,
          refund: Number(s.refund) || 0,
          net: Number(s.net) || 0
        }
        if (data.message && !this.list.length) {
          this.error = data.message
        }
        this.schedulePoll()
      } catch (e) {
        if (!silent) {
          this.error = (e && e.message) || '加载失败，请确认已部署 /idoing_usage 并已登录'
          this.list = []
        }
        this.schedulePoll()
      } finally {
        if (!silent) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.usage__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.usage__head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
@media (min-width: 900px) {
  .stats {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.stat {
  padding: 16px 18px;
}
.stat__label {
  font-size: 12px;
  color: var(--muted);
}
.stat__value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.03em;
}
.stat__link {
  display: inline-block;
  margin-top: 10px;
  font-size: 12px;
  color: var(--accent);
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.chip {
  border: 1px solid var(--line);
  background: transparent;
  color: var(--muted);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}
.chip.is-active {
  color: var(--accent);
  border-color: rgba(125, 255, 179, 0.35);
  background: var(--accent-dim);
}
.table-wrap {
  padding: 0;
  overflow: auto;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 960px;
}
.table th,
.table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
}
.table th {
  color: var(--muted);
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
}
.nowrap {
  white-space: nowrap;
}
.task-id {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.prompt {
  max-width: 220px;
  word-break: break-word;
}
.desc {
  max-width: 200px;
  word-break: break-word;
}
.sub {
  margin-top: 4px;
  font-size: 11px;
}
.btn-result {
  border: 1px solid rgba(125, 255, 179, 0.35);
  background: var(--accent-dim);
  color: var(--accent);
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.btn-result:hover {
  filter: brightness(1.08);
}
.tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
.tag-out {
  color: #ff8e8e;
  background: rgba(255, 107, 107, 0.12);
}
.tag-in {
  color: #7dffb3;
  background: rgba(125, 255, 179, 0.12);
}
.status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}
.status--ok {
  color: #7dffb3;
  background: rgba(125, 255, 179, 0.12);
}
.status--err {
  color: #ff8e8e;
  background: rgba(255, 107, 107, 0.12);
}
.status--wait {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.12);
}
.status--muted {
  color: var(--muted);
  background: rgba(255, 255, 255, 0.06);
}
.empty {
  padding: 28px;
  text-align: center;
  color: var(--muted);
}
.flash {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
}
.flash.is-err {
  color: var(--danger);
  border: 1px solid rgba(255, 107, 107, 0.35);
  background: rgba(255, 107, 107, 0.08);
}
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.muted {
  color: var(--muted);
}
.ok {
  color: #34d399;
}
.err {
  color: var(--danger);
}

/* 详情弹窗 */
.detail-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.detail {
  width: min(980px, 100%);
  max-height: min(88vh, 820px);
  overflow: auto;
  background: #f5f6f8;
  color: #1f2937;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}
.detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: #f5f6f8;
  z-index: 1;
}
.detail__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.detail__close {
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  padding: 0 4px;
}
.detail__body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}
@media (min-width: 860px) {
  .detail__body {
    grid-template-columns: 1fr 1fr;
  }
  .detail__col--in {
    border-right: 1px solid #e5e7eb;
  }
}
.detail__col {
  padding: 18px 20px 22px;
  min-width: 0;
}
.detail__col-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.detail__col-title {
  font-size: 15px;
  font-weight: 600;
}
.detail__ghost-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
}
.detail__ghost-btn:hover {
  background: #f9fafb;
}
.detail__block {
  margin-bottom: 16px;
}
.detail__label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}
.detail__prompt {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 160px;
  overflow: auto;
}
.detail__params {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 4px 0;
}
.detail__param {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  font-size: 13px;
  border-bottom: 1px solid #f3f4f6;
}
.detail__param:last-child {
  border-bottom: none;
}
.detail__param-k {
  color: #6b7280;
  flex-shrink: 0;
}
.detail__param-v {
  color: #111827;
  text-align: right;
  word-break: break-all;
}
.detail__meta {
  margin-top: 8px;
  display: grid;
  gap: 8px;
}
.detail__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
}
.detail__meta-row span:last-child {
  color: #4b5563;
  text-align: right;
  word-break: break-all;
}
.detail__preview {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 16px;
}
.detail__media {
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  background: #111;
}
.detail__preview-empty {
  padding: 48px 16px;
  font-size: 13px;
}
.detail__url-row {
  display: flex;
  gap: 8px;
}
.detail__url {
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  color: #374151;
}
.detail__icon-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 12px;
  cursor: pointer;
  color: #374151;
  white-space: nowrap;
}
.detail__icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.detail__source-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 14px;
  width: 100%;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  color: #374151;
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  text-decoration: none;
}
.detail__source-btn:hover {
  background: #e5e7eb;
}
</style>
